import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { jaanoFeatures } from "../../data/jaanoFeatures";

const Section5 = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const totalSlides = jaanoFeatures.length;
  const intervalRef = useRef(null);

  const getVisibleSlides = () => {
    if (window.innerWidth >= 1536) return 3;
    if (window.innerWidth >= 768) return 2;
    return 1;
  };

  const [visibleSlides, setVisibleSlides] = useState(getVisibleSlides());
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setVisibleSlides(getVisibleSlides());
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getSlidesToShow = () => {
    if (currentIndex + visibleSlides > totalSlides) {
      return [
        ...jaanoFeatures.slice(currentIndex, totalSlides),
        ...jaanoFeatures.slice(0, (currentIndex + visibleSlides) % totalSlides),
      ];
    }
    return jaanoFeatures.slice(currentIndex, currentIndex + visibleSlides);
  };

  const resetInterval = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + visibleSlides) % totalSlides);
    }, 5000);
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + visibleSlides) % totalSlides);
    resetInterval();
  };

  const prevSlide = () => {
    setCurrentIndex(
      (prev) => (prev - visibleSlides + totalSlides) % totalSlides
    );
    resetInterval();
  };

  useEffect(() => {
    resetInterval();
    return () => clearInterval(intervalRef.current);
  }, []);

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const deltaX = touchEndX.current - touchStartX.current;
    if (deltaX > 50) prevSlide();
    if (deltaX < -50) nextSlide();
  };

  return (
    <section
      className="relative w-full  flex flex-col items-center justify-center py-10 px-4 sm:px-10 md:px-20 bg-gradient-to-b from-white to-blue-50"
      onTouchStart={handleTouchStart}
      onTouchMove={(e) => (touchEndX.current = e.touches[0].clientX)}
      onTouchEnd={handleTouchEnd}>
      <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center text-darkprimary">
        What Makes Jaano Awesome?
      </h2>

      <div className="relative w-full max-w-7xl overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            className={`grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-6`}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.5 }}>
            {getSlidesToShow().map((feature) => (
              <motion.div
                key={feature.id}
                whileHover={{ scale: 1.02 }}
                className="flex flex-col items-center text-center bg-white p-8 rounded-2xl shadow-lg transition-all duration-300 border border-gray-200">
                <div className="text-4xl text-primary mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-darkprimary">
                  {feature.title}
                </h3>
                <p className="mt-2 text-gray-600 text-sm sm:text-base">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        {!isMobile && (
          <>
            <button
              onClick={prevSlide}
              className="absolute top-1/2 left-2 transform -translate-y-1/2 p-3 bg-white shadow-lg rounded-full hover:bg-blue-100 transition">
              <FaArrowLeft className="text-xl text-primary" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute top-1/2 right-2 transform -translate-y-1/2 p-3 bg-white shadow-lg rounded-full hover:bg-blue-100 transition">
              <FaArrowRight className="text-xl text-primary" />
            </button>
          </>
        )}
      </div>

      {/* Dot Indicators */}
      <div className="flex mt-6 space-x-3">
        {Array.from({ length: Math.ceil(totalSlides / visibleSlides) }).map(
          (_, index) => (
            <motion.div
              key={index}
              className={`h-3 w-3 rounded-full transition-all duration-300 cursor-pointer ${
                currentIndex === index * visibleSlides
                  ? "bg-primary scale-110"
                  : "bg-gray-400"
              }`}
              onClick={() => {
                setCurrentIndex(index * visibleSlides);
                resetInterval();
              }}
              whileHover={{ scale: 1.3 }}
            />
          )
        )}
      </div>
    </section>
  );
};

export default Section5;
